import {
  IHomeVideoHeroFields,
  SpecificLocaleFields,
} from "~/@types/generated/contentful";
import { motion, useScroll, useTransform } from "framer-motion";
import { useMedia, useWindowSize } from "react-use";
import clsx from "clsx";
import FlutterText from "../animations/flutterText";
import { ContentfulCenteredImage } from "../shared";
import { PAGE_TRANSITION_DELAY } from "~/contexts/PageTransitionProvider";
import { useNavigation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "../icons";

declare global {
  interface Window {
    _wq: any[];
  }
}

interface WistiaVideoProps {
  bind: (event: string, callback: () => void) => void;
  pause: () => void;
  play: () => void;
}

type HomeVideoHeroProps = {
  fields: SpecificLocaleFields<IHomeVideoHeroFields>;
};

const TextBox = ({
  fields,
  className = "",
  show = true,
}: {
  fields: SpecificLocaleFields<IHomeVideoHeroFields>;
  className?: string;
  show?: boolean;
}) => (
  <div
    className={clsx(
      className,
      "absolute inset-0 flex h-full w-full flex-col justify-center",
    )}
  >
    <div className="flex w-full flex-col items-center justify-center">
      <h1 className="eyebrows col-span-full mb-6 text-center lg:col-span-4 lg:col-start-5">
        {fields.eyebrow}
      </h1>
      {show && (
        <FlutterText
          text={fields.title}
          className="h1 col-span-full max-w-[1000px] text-center lg:col-span-8 lg:col-start-3"
          as="h2"
          aria-label={fields.title}
        />
      )}
    </div>
  </div>
);

interface DynamicVideoProps {
  fields: SpecificLocaleFields<IHomeVideoHeroFields>;
}

const WistiaHeroVideo = ({
  wistiaId,
  className,
}: {
  wistiaId: string;
  className: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    window._wq = window._wq || [];
    window._wq.push({
      id: wistiaId,
      options: {
        autoPlay: true,
        silentAutoPlay: "allow",
        muted: true,
        endVideoBehavior: "loop",
        playsinline: true,
        controlsVisibleOnLoad: false,
        playButton: false,
        seo: false,
      },
      onReady: (video: WistiaVideoProps) => {
        video.bind("play", () => setIsPlaying(true));
        video.bind("pause", () => setIsPlaying(false));
      },
    });

    const script1 = document.createElement("script");
    script1.src = `https://fast.wistia.com/embed/medias/${wistiaId}.jsonp`;
    script1.async = true;
    container.appendChild(script1);

    const script2 = document.createElement("script");
    script2.src = "https://fast.wistia.com/assets/external/E-v1.js";
    script2.async = true;
    container.appendChild(script2);

    return () => {
      container.innerHTML = "";
    };
  }, [wistiaId]);

  const togglePlayPause = () => {
    window._wq = window._wq || [];
    window._wq.push({
      id: wistiaId,
      onReady: (video: WistiaVideoProps) => {
        if (isPlaying) {
          video.pause();
        } else {
          video.play();
        }
      },
    });
    setIsPlaying((prev) => !prev);
  };

  return (
    <div ref={containerRef} className={className}>
      <div
        className={`wistia_embed wistia_async_${wistiaId} videoFoam=false fitStrategy=cover`}
        style={{
          position: "absolute",
          inset: 0,
          height: "100%",
          width: "100%",
        }}
      />
      <button
        onClick={togglePlayPause}
        className="absolute bottom-4 right-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white text-white transition-all duration-200 lg:bottom-8"
        aria-label={isPlaying ? "Pause video" : "Play video"}
      >
        {isPlaying ? (
          <Pause className="h-6 w-6" />
        ) : (
          <Play className="h-6 w-6" />
        )}
      </button>
    </div>
  );
};

const DynamicVideo = ({ fields }: DynamicVideoProps) => {
  const navigation = useNavigation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const isLarge = useMedia("(min-width: 1024px)", false);

  const desktopWistiaId = fields.video?.items?.[0]?.hashed_id as
    | string
    | undefined;
  const mobileWistiaId = fields.mobileVideo?.items?.[0]?.hashed_id as
    | string
    | undefined;
  const activeWistiaId = isLarge
    ? desktopWistiaId
    : (mobileWistiaId ?? desktopWistiaId);

  useEffect(() => {
    setShowVideo(true);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [isLarge]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  if (navigation.state !== "loading" && showVideo) {
    if (activeWistiaId) {
      return (
        <WistiaHeroVideo
          key={activeWistiaId}
          wistiaId={activeWistiaId}
          className={
            isLarge
              ? "relative flex h-full w-full overflow-hidden lgmax:hidden"
              : "absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden lg:hidden"
          }
        />
      );
    }

    return (
      <div
        className={
          !isLarge
            ? "absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden lg:hidden"
            : "relative flex aspect-video h-auto w-full overflow-hidden lgmax:hidden xl:h-full"
        }
      >
        <video
          ref={videoRef}
          id={!isLarge ? "video" : "video-large"}
          className={`absolute top-0 w-full object-cover ${
            !isLarge ? "h-auto min-h-[90vh]" : "h-full lgmax:hidden"
          }`}
          playsInline
          autoPlay
          muted
          loop
        >
          <img
            src={fields.fallbackImage.fields.image?.fields?.file?.url}
            title="Your browser does not support the <video> tag"
            alt={fields.fallbackImage.fields.alt}
          />
          <track
            src="/noCaptions.vtt"
            kind="captions"
            srcLang="en"
            label="english captions"
          />
          Unsupported video.
        </video>
        <button
          onClick={togglePlayPause}
          className="absolute bottom-4 right-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white text-white transition-all duration-200 lg:bottom-8"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause className="h-6 w-6" />
          ) : (
            <Play className="h-6 w-6" />
          )}
        </button>
      </div>
    );
  }

  return (
    <ContentfulCenteredImage
      className="absolute inset-0 h-full w-full lg:hidden"
      image={fields.fallbackImage.fields}
    />
  );
};

export default function HomeVideoHero({ fields }: HomeVideoHeroProps) {
  const { height } = useWindowSize();
  const { scrollY } = useScroll();
  const opacityTransform = useTransform(scrollY, [0, height * 0.75], [1, 0]);

  const scrollToContent = () => {
    window.scrollTo({
      top: height,
      behavior: "smooth",
    });
  };

  return (
    <section className="relative mx-auto h-[calc(100vh-80px)] min-h-hero-min w-screen lg:h-[calc(100vh-96px)]">
      {/* // height is screen minus nav minus space for bottom explore eyebrow */}
      <motion.div
        className="relative h-[calc(100%-40px)] w-full"
        style={{
          opacity: opacityTransform,
        }}
      >
        <div className="absolute left-4 flex h-full w-[calc(100%-32px)] justify-center lg:left-9 lg:w-[calc(100%-72px)]">
          <TextBox className="z-10 text-white" fields={fields} />
          <div className="absolute inset-0 flex h-full w-full items-center justify-center overflow-hidden text-white">
            <DynamicVideo fields={fields} />
          </div>

          <motion.div
            className="absolute inset-0 z-10 flex h-full bg-white"
            initial={{
              clipPath:
                "polygon(0% 0%, 0% 100%, 49.5% 100%, 49.5% 0%, 50.5% 0%, 50.5% 100%, 49.5% 100%, 49.5% 100%, 100% 100%, 100% 0%)",
              WebkitClipPath:
                "polygon(0% 0%, 0% 100%, 49.5% 100%, 49.5% 0%, 50.5% 0%, 50.5% 100%, 49.5% 100%, 49.5% 100%, 100% 100%, 100% 0%)",
            }}
            whileInView={{
              clipPath:
                "polygon(0% 0%, 0% 100%, 0% 100%, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 100%, 100% 100%, 100% 0%)",
              WebkitClipPath:
                "polygon(0% 0%, 0% 100%, 0% 100%, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 100%, 100% 100%, 100% 0%)",
            }}
            viewport={{ once: true, amount: "some" }}
            transition={{
              duration: 1.3,
              ease: [1, 0.08, 0.29, 0.67],
              delay: PAGE_TRANSITION_DELAY / 1000,
            }}
          ></motion.div>
        </div>
      </motion.div>

      <div className="flex-center z-10 col-span-full h-10 w-full">
        <motion.button
          className="min-w-max p-4 text-14 uppercase"
          style={{
            opacity: opacityTransform,
          }}
          onClick={scrollToContent}
          whileHover="hover"
          whileInView="noHover"
        >
          Explore
          <motion.div
            className="flex border-b border-solid border-black content-['']"
            variants={{
              hover: {
                transform: "scaleX(1)",
              },
              noHover: {
                transform: "scaleX(0)",
              },
            }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
            }}
          />
        </motion.button>
      </div>
    </section>
  );
}
